import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { achievements } from '../data/achievements';

const { FiArrowLeft, FiTrophy, FiLock, FiStar, FiFilter } = FiIcons;

function AchievementsScreen({ onBack }) {
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [filter, setFilter] = useState('all');
  const [playerStats, setPlayerStats] = useState({});

  useEffect(() => {
    loadAchievements();
    loadPlayerStats();
  }, []);

  const loadAchievements = () => {
    const unlocked = JSON.parse(localStorage.getItem('unlockedAchievements') || '[]');
    setUnlockedAchievements(unlocked);
  };

  const loadPlayerStats = () => {
    const stats = JSON.parse(localStorage.getItem('playerStats') || '{}');
    setPlayerStats(stats);
  };

  const isAchievementUnlocked = (achievementId) => {
    return unlockedAchievements.some(a => a.id === achievementId);
  };

  const getFilteredAchievements = () => {
    switch (filter) {
      case 'unlocked':
        return achievements.filter(a => isAchievementUnlocked(a.id));
      case 'locked':
        return achievements.filter(a => !isAchievementUnlocked(a.id));
      default:
        return achievements;
    }
  };

  const getRarityColor = (rarity) => {
    switch (rarity) {
      case 'common': return 'from-gray-500 to-gray-600';
      case 'rare': return 'from-blue-500 to-blue-600';
      case 'epic': return 'from-purple-500 to-purple-600';
      case 'legendary': return 'from-yellow-500 to-orange-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getProgressForAchievement = (achievement) => {
    if (isAchievementUnlocked(achievement.id)) return 100;
    
    switch (achievement.type) {
      case 'score':
        const highScore = Math.max(...JSON.parse(localStorage.getItem('colorMixScores') || '[]').map(s => s.score), 0);
        return Math.min((highScore / achievement.target) * 100, 100);
      case 'accuracy':
        return Math.min((playerStats.bestAccuracy || 0), 100);
      case 'streak':
        return Math.min(((playerStats.bestStreak || 0) / achievement.target) * 100, 100);
      case 'games':
        return Math.min(((playerStats.totalGames || 0) / achievement.target) * 100, 100);
      case 'perfect':
        return Math.min(((playerStats.perfectMatches || 0) / achievement.target) * 100, 100);
      case 'speed':
        return Math.min(((playerStats.fastAnswers || 0) / achievement.target) * 100, 100);
      default:
        return 0;
    }
  };

  const totalUnlocked = unlockedAchievements.length;
  const totalXP = unlockedAchievements.reduce((sum, a) => sum + a.points, 0);
  const completionRate = Math.round((totalUnlocked / achievements.length) * 100);

  const filters = [
    { id: 'all', label: 'All', count: achievements.length },
    { id: 'unlocked', label: 'Unlocked', count: totalUnlocked },
    { id: 'locked', label: 'Locked', count: achievements.length - totalUnlocked }
  ];

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onBack}
            className="bg-white/20 backdrop-blur-sm p-3 rounded-xl"
          >
            <SafeIcon icon={FiArrowLeft} className="text-xl text-white" />
          </motion.button>
          
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <SafeIcon icon={FiTrophy} className="text-yellow-400" />
              Achievements
            </h1>
          </div>
          
          <div className="w-12" />
        </div>

        {/* Progress Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-6 mb-6 border border-purple-400/30"
        >
          <div className="text-center mb-4">
            <h2 className="text-white font-bold text-xl mb-2">Progress Overview</h2>
            <div className="text-4xl font-bold text-white">
              {completionRate}%
            </div>
            <p className="text-gray-300 text-sm">Completion Rate</p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-yellow-400">{totalUnlocked}</div>
              <p className="text-gray-300 text-sm">Unlocked</p>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-400">{totalXP}</div>
              <p className="text-gray-300 text-sm">Total XP</p>
            </div>
          </div>

          <div className="mt-4">
            <div className="w-full bg-gray-700 rounded-full h-2">
              <motion.div 
                className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${completionRate}%` }}
                transition={{ duration: 1, delay: 0.5 }}
              />
            </div>
          </div>
        </motion.div>

        {/* Filter Tabs */}
        <div className="flex bg-white/10 backdrop-blur-sm rounded-2xl p-2 mb-6">
          {filters.map((filterOption) => (
            <motion.button
              key={filterOption.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setFilter(filterOption.id)}
              className={`flex-1 py-2 px-3 rounded-xl transition-all duration-200 ${
                filter === filterOption.id
                  ? 'bg-white text-purple-900 font-bold'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              <div className="text-center">
                <div className="text-sm font-medium">{filterOption.label}</div>
                <div className="text-xs opacity-70">({filterOption.count})</div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Achievements List */}
        <div className="space-y-4">
          {getFilteredAchievements().map((achievement, index) => {
            const isUnlocked = isAchievementUnlocked(achievement.id);
            const progress = getProgressForAchievement(achievement);
            
            return (
              <motion.div
                key={achievement.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={`bg-white/10 backdrop-blur-sm rounded-2xl p-4 border-2 ${
                  isUnlocked 
                    ? `bg-gradient-to-r ${getRarityColor(achievement.rarity)}/20 border-white/30` 
                    : 'border-gray-600/30'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`text-4xl ${!isUnlocked ? 'grayscale opacity-50' : ''}`}>
                    {isUnlocked ? achievement.emoji : '🔒'}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className={`font-bold ${isUnlocked ? 'text-white' : 'text-gray-400'}`}>
                        {achievement.name}
                      </h3>
                      {isUnlocked && (
                        <div className="flex items-center gap-1">
                          <SafeIcon icon={FiStar} className="text-yellow-400 text-sm" />
                          <span className="text-yellow-400 text-sm">+{achievement.points}</span>
                        </div>
                      )}
                    </div>
                    
                    <p className={`text-sm mb-2 ${isUnlocked ? 'text-gray-300' : 'text-gray-500'}`}>
                      {achievement.description}
                    </p>
                    
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-700 rounded-full h-2">
                        <motion.div 
                          className={`h-2 rounded-full ${
                            isUnlocked 
                              ? `bg-gradient-to-r ${getRarityColor(achievement.rarity)}` 
                              : 'bg-gray-600'
                          }`}
                          initial={{ width: 0 }}
                          animate={{ width: `${progress}%` }}
                          transition={{ duration: 0.8, delay: index * 0.1 }}
                        />
                      </div>
                      <span className={`text-xs font-medium ${isUnlocked ? 'text-white' : 'text-gray-500'}`}>
                        {Math.round(progress)}%
                      </span>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <span className={`text-xs uppercase tracking-wide ${
                        isUnlocked ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        {achievement.rarity}
                      </span>
                      {achievement.reward && isUnlocked && (
                        <span className="text-xs text-yellow-400">
                          🎁 {achievement.reward}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {getFilteredAchievements().length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiTrophy} className="text-6xl text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No achievements found</p>
            <p className="text-gray-500 text-sm">
              {filter === 'unlocked' ? 'Start playing to unlock achievements!' : 'Keep playing to unlock more!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AchievementsScreen;